import { formatDateToYYYYMMDD, parseLocalDate } from '@/utils/dateUtils';

/**
 * Browser preference: the last stay dates and guests the user picked in the booking widget.
 * Stored per device so the next hotel they open starts from the same search.
 */
export const SAVED_STAY_SEARCH_STORAGE_KEY = 'globethrivers_saved_stay_search';

export interface SavedStaySearch {
  checkIn: string;
  checkOut: string;
  adults: number;
  rooms: number;
}

/** Returns the saved search, or null when missing, malformed, or the check-in date has passed. */
export function readSavedStaySearch(now: Date = new Date()): SavedStaySearch | null {
  if (typeof window === 'undefined') {
    return null;
  }
  let raw: string | null;
  try {
    raw = localStorage.getItem(SAVED_STAY_SEARCH_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<SavedStaySearch>;
    const checkInDate = parseLocalDate(parsed.checkIn);
    const checkOutDate = parseLocalDate(parsed.checkOut);
    const today = parseLocalDate(now);
    if (!checkInDate || !checkOutDate || !today) {
      return null;
    }
    if (checkInDate < today || checkOutDate <= checkInDate) {
      return null;
    }
    const adults = Number.isInteger(parsed.adults) && parsed.adults! >= 1 ? parsed.adults! : 2;
    const rooms = Number.isInteger(parsed.rooms) && parsed.rooms! >= 1 ? parsed.rooms! : 1;
    return {
      checkIn: formatDateToYYYYMMDD(checkInDate),
      checkOut: formatDateToYYYYMMDD(checkOutDate),
      adults,
      rooms,
    };
  } catch {
    return null;
  }
}

export function writeSavedStaySearch(search: SavedStaySearch): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(SAVED_STAY_SEARCH_STORAGE_KEY, JSON.stringify(search));
  } catch {
    // Storage can be unavailable (private mode, quota) — the widget falls back to defaults.
  }
}
